import { Tag } from '@carbon/react';
import { CheckmarkFilled, CircleDash, Incomplete } from '@carbon/react/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import {
  DataTable,
  type DataTableColumnDef,
  type TableState,
} from 'components/data-table';
import { Link } from 'components/link';

import { slotListAtomFamily } from '../atoms';
import type { SlotCertificationStatus, SlotInterestRow } from '../types';
import {
  resolveSortAttribute,
  SLOT_STATUS_LABELS,
  slotStatus,
  subtractsFraction,
} from '../utils';
import styles from './slot-status-table.module.scss';

const PAGE_SIZE = 10;

// The DataTable wrapper's generic is `<TData extends Record<string, unknown>>`;
// SlotInterestRow lacks the implicit index signature, so consumers cast.
type SlotRow = SlotInterestRow & Record<string, unknown>;

type SlotSearchAtom = ReturnType<typeof slotListAtomFamily>;

interface SlotStatusTableProps {
  searchAtom: SlotSearchAtom;
  workspaceId: number;
  /** When true, only slots changed this session are listed (Promote modal). */
  changedOnly?: boolean;
}

const columnHelper = createColumnHelper<SlotRow>();

const STATUS_ICON: Record<
  SlotCertificationStatus,
  { Icon: typeof CircleDash; iconClass: string }
> = {
  not_started: { Icon: CircleDash, iconClass: styles.iconPending },
  in_progress: { Icon: Incomplete, iconClass: styles.iconPartial },
  certified: { Icon: CheckmarkFilled, iconClass: styles.iconReconciled },
};

/**
 * Status cell for the promote / tasks slot table, mirroring the main
 * comparison table: promoted-grain status (icon + label) plus a red On Hold
 * tag when the slot has an active hold. A slot that is partially certified AND
 * held this session therefore reads as both (e.g. "In Progress" + On Hold),
 * with the certified count carried in the Subtracts column (INS-9896). The BE
 * also caps a held slot's status to in_progress, so it never reads "Certified"
 * while a hold is open.
 *
 * @param {{ row: SlotInterestRow }} props - The slot row to render.
 * @return {JSX.Element} The status cell.
 */
export const SlotStatusCell = ({
  row,
}: {
  row: SlotInterestRow;
}): JSX.Element => {
  const status = slotStatus(row);
  const { Icon, iconClass } = STATUS_ICON[status];
  return (
    <span className={styles.statusCell}>
      <Icon className={iconClass} />
      <span className={styles.statusLabel}>{SLOT_STATUS_LABELS[status]}</span>
      {row.onHold && (
        <Tag type="red" size="sm">
          Needs Further Review
        </Tag>
      )}
    </span>
  );
};

const columns = [
  columnHelper.accessor((row) => row.confirmation, {
    id: 'status',
    header: 'Status',
    cell: (info) => <SlotStatusCell row={info.row.original} />,
    size: 200,
  }),
  // Live session certification progress ("2/4 subtracts"), mirroring the main
  // comparison table — so a held slot still shows how many tracts it certified.
  columnHelper.display({
    id: 'tracts',
    header: 'Subtracts',
    cell: (info) => subtractsFraction(info.row.original) ?? '—',
    size: 130,
  }),
  columnHelper.accessor('slotId', {
    id: 'slotId',
    header: 'UWI',
    cell: (info) => (
      <Link to={`/wells/${info.getValue()}`} openInNewTab>
        {info.getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor('wellName', {
    id: 'wellName',
    header: 'Well Name',
    enableSorting: false,
  }),
] as DataTableColumnDef<SlotRow>[];

/**
 * Read-only three-column DataTable showing each slot's reconciliation status.
 * Server-paginated + server-searched + server-sorted against
 * `POST /queries/interest_certification_workspace_slots` via the per-surface
 * `searchAtom` prop. Shared by the Promote modal and the Tasks side panel —
 * each consumer passes its own atom so they paginate and search independently.
 *
 * @param {SlotStatusTableProps} props - Component props.
 * @param {SlotSearchAtom} props.searchAtom - Per-surface searchAtom (promote
 *   vs tasks).
 * @param {number} props.workspaceId - Active workspace id, sent in the query
 *   body.
 * @return {JSX.Element} The rendered table assembly.
 */
export const SlotStatusTable = ({
  searchAtom,
  workspaceId,
  changedOnly = false,
}: SlotStatusTableProps): JSX.Element => {
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  useEffect(() => {
    // `searchAtomPair` was instantiated with no `defaultPayload`, so returning
    // `undefined` from the updater hits the writer's `if (!searchPayload) return`
    // guard and skips the fetch — used here to keep an already-loaded surface
    // from refetching when it re-mounts.
    setSearchPayload((prev) => {
      if (prev) return undefined;
      return { workspaceId, page: 1, pageSize: PAGE_SIZE, changedOnly };
    });
  }, [workspaceId, changedOnly, setSearchPayload]);

  const handleStateChange = useCallback(
    (state: TableState) => {
      const sort = state.sorting[0];
      setSearchPayload(() => ({
        workspaceId,
        changedOnly,
        page: state.page,
        pageSize: state.pageSize,
        text: state.globalFilter || undefined,
        sortAttribute: sort ? resolveSortAttribute(sort.id) : undefined,
        sortDirection: sort ? (sort.desc ? 'DESC' : 'ASC') : undefined,
      }));
    },
    [workspaceId, changedOnly, setSearchPayload]
  );

  const data = useMemo(
    () => (searchResult.data?.results ?? []) as SlotRow[],
    [searchResult.data?.results]
  );
  const totalCount = searchResult.data?.totalCount ?? 0;

  return (
    <DataTable<SlotRow>
      data={data}
      columns={columns}
      getRowId={(row) => String(row.workspaceSlotId)}
      initialTableState={{ pageSize: PAGE_SIZE }}
      isLoading={searchResult.loading ?? false}
      error={searchResult.error ?? null}
      scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
      config={{
        columnResizing: false,
        columnReordering: false,
        columnPinning: false,
        columnVisibility: false,
        columnFilters: false,
      }}
      onPaginationChange={handleStateChange}
      onSortChange={handleStateChange}
      onGlobalSearchChange={handleStateChange}
      searchPlaceholder="Search by UWI or well name"
    />
  );
};
